import React, { useState } from 'react';
import { BgImage } from '../components/Img';
import { IMG } from '../images';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    location: '',
    serviceType: '',
    message: '',
    preferredContact: 'email',
    urgent: false,
    website: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);


  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Send to backend API
      const response = await axios.post('/api/quote', formData);
      
      if (response.data.success) {
        setSubmitted(true);
        // Lead event for Google Ads / Analytics (no personal data is sent)
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'generate_lead', { event_category: 'quote_form', urgent: formData.urgent ? 'yes' : 'no' });
        }
        const el = document.getElementById('quote-form');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to send your request. Please try calling us directly at 0426 920 301');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section 
        className="hero" 
        style={{
          minHeight: '50vh'
        }}
      >
        <BgImage image={IMG.hero} position="60% 50%" priority />
        <div className="hero-content">
          <h1>Contact ProClimb Solutions</h1>
          <p>Get in touch for a free quote and consultation</p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="quote-form" className="section">
        <div className="container">
          <div className="contact-content">
            {/* Contact Form */}
            <div>
              <h3 style={{ fontSize: '1.8rem', color: 'var(--navy-blue)', marginBottom: '1rem' }}>
                Request a Quote
              </h3>
              <p style={{ color: 'var(--steel-gray)', marginBottom: '2rem' }}>
                Tell us about your building, your challenges and the services you need. We'll review your requirements and provide a clear, tailored quote.
              </p>
              
              {submitted ? (
                <div className="callout" role="status" style={{ textAlign: 'left' }}>
                  <CheckCircle size={44} style={{ color: 'var(--safety-orange)', marginBottom: '0.75rem' }} aria-hidden="true" />
                  <h3>Thank you, your request has been sent</h3>
                  <p>
                    We have received your enquiry and will contact you within 24 hours. We have also emailed you a copy for your records.
                    If it is urgent or involves a safety risk, please call us now on{' '}
                    <a href="tel:0426920301" style={{ color: 'var(--safety-orange)', fontWeight: 700 }}>0426 920 301</a>.
                  </p>
                  <Link to="/" className="btn-outline">Back to home</Link>
                </div>
              ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
                  <label htmlFor="website">Leave this field empty</label>
                  <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" value={formData.website} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company">Company / Building Name</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="location">Location</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="e.g., Sydney CBD, Parramatta"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="serviceType">Type of Service Required</label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                  >
                    <option value="">Select a service</option>
                    <option value="window-cleaning">High Rise Window Cleaning</option>
                    <option value="facade-cleaning">Facade Cleaning</option>
                    <option value="remedial">Remedial Work</option>
                    <option value="glazing">Glazing Services</option>
                    <option value="height-safety">Height Safety</option>
                    <option value="bird-control">Bird Control</option>
                    <option value="emergency">Emergency Response</option>
                    <option value="premium">Luxury Homes (private residential)</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <label htmlFor="urgent" style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', padding: '0.9rem 1rem', background: 'var(--light-gray)', border: '1px solid var(--border-color)', cursor: 'pointer' }}>
                  <input type="checkbox" id="urgent" name="urgent" checked={formData.urgent} onChange={handleChange} style={{ marginTop: '4px', width: '18px', height: '18px', accentColor: 'var(--safety-orange)' }} />
                  <span style={{ fontSize: '0.95rem' }}>
                    <strong style={{ color: 'var(--navy-blue)' }}>This is urgent.</strong> For an immediate safety risk, please also call 0426 920 301.
                  </span>
                </label>

                <div className="form-group">
                  <label htmlFor="message">Brief Description of the Job *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    required
                  ></textarea>
                </div>

                <div className="form-group">
                  <label htmlFor="preferredContact">Preferred Time for Contact</label>
                  <select
                    id="preferredContact"
                    name="preferredContact"
                    value={formData.preferredContact}
                    onChange={handleChange}
                  >
                    <option value="email">Email</option>
                    <option value="phone-morning">Phone - Morning (8am-12pm)</option>
                    <option value="phone-afternoon">Phone - Afternoon (12pm-5pm)</option>
                    <option value="phone-anytime">Phone - Anytime</option>
                  </select>
                </div>

                <button 
                  type="submit" 
                  className="btn-primary" 
                  style={{ width: '100%', marginTop: '1rem' }}
                  disabled={isSubmitting}
                >
                  <Send size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} />
                  {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                </button>
                <p style={{ fontSize: '0.85rem', color: 'var(--steel-gray)', marginTop: '0.25rem' }}>
                  By sending this form you agree to us contacting you about your enquiry. See our{' '}
                  <Link to="/privacy-policy" style={{ color: 'var(--navy-blue)', fontWeight: 600 }}>Privacy Policy</Link>.
                </p>
              </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="contact-info">
              <h3>Get In Touch</h3>
              <p style={{ color: 'var(--steel-gray)', marginBottom: '2rem' }}>
                If your situation is urgent or involves an immediate safety risk, please call us directly for a rapid response.
              </p>

              <ul className="contact-details">
                <li>
                  <Phone size={24} style={{ color: 'var(--safety-orange)' }} />
                  <div>
                    <strong style={{ display: 'block', color: 'var(--navy-blue)', marginBottom: '0.25rem' }}>Phone</strong>
                    <a href="tel:0426920301" style={{ color: 'var(--steel-gray)', textDecoration: 'none' }}>
                      0426 920 301
                    </a>
                    <p style={{ fontSize: '0.9rem', color: 'var(--steel-gray)', marginTop: '0.25rem' }}>
                      24/7 Emergency Line
                    </p>
                  </div>
                </li>

                <li>
                  <Mail size={24} style={{ color: 'var(--safety-orange)' }} />
                  <div>
                    <strong style={{ display: 'block', color: 'var(--navy-blue)', marginBottom: '0.25rem' }}>Email</strong>
                    <a href="mailto:info@proclimbsolutions.com.au" style={{ color: 'var(--steel-gray)', textDecoration: 'none' }}>
                      info@proclimbsolutions.com.au
                    </a>
                  </div>
                </li>

                <li>
                  <MapPin size={24} style={{ color: 'var(--safety-orange)' }} />
                  <div>
                    <strong style={{ display: 'block', color: 'var(--navy-blue)', marginBottom: '0.25rem' }}>Address</strong>
                    <p style={{ color: 'var(--steel-gray)' }}>
                      21 Mascot Drive<br />
                      Eastlakes, Sydney NSW<br />
                      Australia
                    </p>
                  </div>
                </li>

                <li>
                  <Clock size={24} style={{ color: 'var(--safety-orange)' }} />
                  <div>
                    <strong style={{ display: 'block', color: 'var(--navy-blue)', marginBottom: '0.25rem' }}>Hours</strong>
                    <p style={{ color: 'var(--steel-gray)' }}>
                      Monday - Friday: 7:00am - 6:00pm<br />
                      Saturday: 8:00am - 4:00pm<br />
                      Sunday: Emergency only<br />
                      <strong style={{ color: 'var(--safety-orange)' }}>24/7 Emergency Response</strong>
                    </p>
                  </div>
                </li>
              </ul>

              <div style={{ 
                marginTop: '2rem', 
                padding: '1.5rem', 
                background: 'var(--light-gray)', 
                border: '1px solid var(--border-color)' 
              }}>
                <h4 style={{ color: 'var(--navy-blue)', marginBottom: '0.75rem' }}>Service Areas</h4>
                <p style={{ color: 'var(--steel-gray)', fontSize: '0.95rem' }}>
                  We are based in New South Wales and regularly work in Sydney, Newcastle, Wollongong, the Blue Mountains and surrounding regions. For specialised projects, we can travel across Australia.{' '}
                  <Link to="/areas" style={{ color: 'var(--navy-blue)', fontWeight: 600 }}>See all service areas</Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency CTA */}
      <section className="section bg-dark">
        <div className="container" style={{ textAlign: 'center', color: 'var(--white)' }}>
          <h2 style={{ color: 'var(--white)', marginBottom: '1rem' }}>Emergency Situation?</h2>
          <p style={{ fontSize: '1.2rem', marginBottom: '2rem', color: '#e5e7eb' }}>
            For immediate assistance with urgent rope access needs, call our emergency line now
          </p>
          <a href="tel:0426920301" className="btn-primary" style={{ fontSize: '1.3rem', padding: '18px 36px' }}>
            <Phone size={24} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} />
            Call Emergency: 0426 920 301
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
